import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import './App.css';

// Login y Home van en el bundle inicial: son la primera pantalla y partirlas
// solo anadiria una ida y vuelta mas antes de poder entrar.
import Login from './pages/Login';
import Home from './pages/Home';

// El resto de paginas se cargan bajo demanda. Antes las 22 viajaban en un unico
// bundle de 1,38 MB que habia que descargar y parsear antes de pintar el login.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Ventas = lazy(() => import('./pages/Ventas'));
const Delivery = lazy(() => import('./pages/Delivery'));
const Laboratorio = lazy(() => import('./pages/Laboratorio'));
const Productos = lazy(() => import('./pages/Productos'));
const Proveedores = lazy(() => import('./pages/Proveedores'));
const Clientes = lazy(() => import('./pages/Clientes'));
const Funcionarios = lazy(() => import('./pages/Funcionarios'));
const Stock = lazy(() => import('./pages/Stock'));
const Flota = lazy(() => import('./pages/Flota'));
const Facturas = lazy(() => import('./pages/Facturas'));
const Usuarios = lazy(() => import('./pages/Usuarios'));
const Sistema = lazy(() => import('./pages/Sistema'));
const Marcas = lazy(() => import('./pages/Marcas'));
const Categorias = lazy(() => import('./pages/Categorias'));
const Permisos = lazy(() => import('./pages/Permisos'));
const HistorialVentas = lazy(() => import('./pages/HistorialVentas'));
const Reportes = lazy(() => import('./pages/Reportes'));
const Gastos = lazy(() => import('./pages/Gastos'));
const BusinessIntelligence = lazy(() => import('./pages/BusinessIntelligence'));
const Presupuestos = lazy(() => import('./pages/Presupuestos'));

const PantallaCarga = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);


// Protected Route Component
const ProtectedRoute = ({ children, permission }) => {
  const { user, loading, hasPermission } = useApp();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Validar permiso si se especifica
  if (permission) {
    // Si permission es array, verificar si tiene al menos uno
    if (Array.isArray(permission)) {
      const hasAnyPermission = permission.some(perm => hasPermission(perm));
      if (!hasAnyPermission) {
        return <Navigate to="/home" replace />;
      }
    } else {
      // Si es string, verificar ese permiso
      if (!hasPermission(permission)) {
        return <Navigate to="/home" replace />;
      }
    }
  }
  
  return children;
};

// Routes with Layout
const AppRoutes = () => {
  const { user } = useApp();
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to="/home" replace /> : <Login />} 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/home" 
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute permission="dashboard.ver">
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/ventas" 
        element={
          <ProtectedRoute permission={['ventas.crear', 'ventas.ver']}>
            <Layout>
              <Ventas />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/delivery" 
        element={
          <ProtectedRoute permission="delivery.ver">
            <Layout>
              <Delivery />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/historial-ventas" 
        element={
          <ProtectedRoute permission="ventas.ver_historial">
            <Layout>
              <HistorialVentas />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/laboratorio" 
        element={
          <ProtectedRoute permission="laboratorio.ver">
            <Layout>
              <Laboratorio />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/productos" 
        element={
          <ProtectedRoute permission="productos.ver">
            <Layout>
              <Productos />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route
        path="/presupuestos"
        element={
          <ProtectedRoute permission="presupuestos.ver">
            <Layout>
              <Presupuestos />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/proveedores"
        element={
          <ProtectedRoute permission="proveedores.ver">
            <Layout>
              <Proveedores />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/clientes" 
        element={
          <ProtectedRoute permission="clientes.ver">
            <Layout>
              <Clientes />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/funcionarios" 
        element={
          <ProtectedRoute permission="funcionarios.ver">
            <Layout>
              <Funcionarios />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/stock" 
        element={
          <ProtectedRoute permission="stock.ver">
            <Layout>
              <Stock />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/flota" 
        element={
          <ProtectedRoute permission="flota.ver">
            <Layout>
              <Flota />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/facturas" 
        element={
          <ProtectedRoute permission="facturas.ver">
            <Layout>
              <Facturas />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/usuarios" 
        element={
          <ProtectedRoute permission="usuarios.ver">
            <Layout>
              <Usuarios />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/marcas" 
        element={
          <ProtectedRoute permission="productos.ver">
            <Layout>
              <Marcas />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/categorias" 
        element={
          <ProtectedRoute permission="productos.ver">
            <Layout>
              <Categorias />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/permisos" 
        element={
          <ProtectedRoute permission="roles.gestionar">
            <Layout>
              <Permisos />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/reportes" 
        element={
          <ProtectedRoute permission="reportes.ver">
            <Layout>
              <Reportes />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/gastos" 
        element={
          <ProtectedRoute permission="gastos.ver">
            <Layout>
              <Gastos />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/business-intelligence" 
        element={
          <ProtectedRoute permission="reportes.business_intelligence">
            <Layout>
              <BusinessIntelligence />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/sistema" 
        element={
          <ProtectedRoute permission="sistema.configurar">
            <Layout>
              <Sistema />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      {/* Default Route */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Suspense fallback={<PantallaCarga />}>
          <AppRoutes />
        </Suspense>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
